import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentStage, TicketStatus, type TicketAttachment } from '@nbh/shared';
import { AttachmentImage } from '../ui/AttachmentImage';
import { PhotoLightbox } from './PhotoLightbox';
import { altKeyFor, photosAtStage } from '../../lib/attachments';

/**
 * The states where the fault is still someone's job to fix.
 *
 * Past RESOLVED the photo of the fault is history rather than instruction, and the
 * gallery and the before-and-after comparison are where it belongs; keeping it in the
 * header would push the verifier's own controls below the fold.
 */
const WORK_PENDING: TicketStatus[] = [
  TicketStatus.Open,
  TicketStatus.Escalated,
  TicketStatus.InProgress,
  // ON_HOLD is still someone's job — D6 makes it a pause, not an ending. A technician
  // who parked a job waiting on a part comes back to it days later needing the fault
  // photo more than anyone, not less.
  TicketStatus.OnHold,
];

export interface TicketIssuePhotoProps {
  attachments: TicketAttachment[];
  status: TicketStatus;
}

/**
 * The photo of the fault, where the person about to do the work will see it (R4).
 *
 * Above the fold in the header, because on a five-year-old Android the alternative is a
 * technician walking to a plant room having read a one-line title and never scrolled to
 * the picture that would have told them which valve.
 *
 * **Renders nothing at all when there is no ISSUE photo** — no frame, no caption, no
 * reserved space, no "add one" prompt. Photos are optional at every stage (D2, D33), so a
 * job without one is an ordinary job and its header must look like one, not like a form
 * with a hole in it.
 */
export function TicketIssuePhoto({ attachments, status }: TicketIssuePhotoProps) {
  const { t } = useTranslation();
  const [viewing, setViewing] = useState(false);

  const photos = photosAtStage(attachments, AttachmentStage.Issue);
  if (!WORK_PENDING.includes(status) || photos.length === 0) return null;

  const [first] = photos;

  return (
    <>
      <button
        type="button"
        onClick={() => setViewing(true)}
        aria-label={t('attachment.open')}
        className="relative mt-3 block w-full overflow-hidden rounded-xl bg-transparent p-0"
      >
        <AttachmentImage
          src={first.url}
          alt={t(altKeyFor(AttachmentStage.Issue))}
          width={first.width}
          height={first.height}
          className="max-h-56 w-full rounded-xl object-cover"
        />

        {/* The rest are one tap away rather than stacked down the header. */}
        {photos.length > 1 && (
          <span className="absolute right-2 bottom-2 rounded-full bg-slate-950/80 px-3 py-1 text-xs font-semibold text-slate-100">
            {t('attachment.more', { count: photos.length - 1 })}
          </span>
        )}
      </button>

      {viewing && <PhotoLightbox photos={photos} onClose={() => setViewing(false)} />}
    </>
  );
}
